// Locations page header: when the cards last updated, a countdown ring to the next automatic
// refresh, "Updating…" while it runs, and a button to update now.
import React, { useEffect, useState } from 'react';
import Icon from '../components/atoms/Icon';
import './locations.css';

const RING = 2 * Math.PI * 9; // circumference of the countdown ring (r = 9)

const ago = (seconds) => {
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ago`;
};

const RefreshStatus = ({ lastUpdatedAt, nextRefreshAt, isRefreshing, onRefresh, intervalMs = 60000 }) => {
  // Re-render every second so the countdown and "ago" stay current
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);
  // Catch up as soon as a new refresh is scheduled (the ticking copy of "now" can be up to a
  // second old, which made a fresh countdown start at 61s)
  useEffect(() => {
    setNow(Date.now());
  }, [nextRefreshAt, lastUpdatedAt]);

  const intervalSeconds = Math.round(intervalMs / 1000);
  const secondsLeft = nextRefreshAt
    ? Math.min(intervalSeconds, Math.max(0, Math.ceil((nextRefreshAt - now) / 1000)))
    : null;
  const elapsed = secondsLeft == null ? 0 : 1 - secondsLeft / (intervalMs / 1000);

  return (
    <div className="loc-refresh" role="status" aria-live="polite">
      {isRefreshing ? (
        <span className="loc-refresh-spinner" aria-hidden="true" />
      ) : (
        <svg className="loc-refresh-ring" width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
          <circle cx="11" cy="11" r="9" className="loc-refresh-ring-bg" />
          <circle
            cx="11"
            cy="11"
            r="9"
            className="loc-refresh-ring-fg"
            strokeDasharray={RING}
            strokeDashoffset={RING * (1 - Math.min(1, Math.max(0, elapsed)))}
          />
        </svg>
      )}
      <span className="loc-refresh-text">
        {isRefreshing ? (
          <strong>Updating…</strong>
        ) : (
          <>
            {lastUpdatedAt && <span>Updated {ago(Math.floor((now - lastUpdatedAt) / 1000))}</span>}
            {secondsLeft != null && (
              <span className="loc-refresh-next">Next update in {secondsLeft}s</span>
            )}
          </>
        )}
      </span>
      <button
        type="button"
        className="loc-refresh-btn"
        onClick={onRefresh}
        disabled={isRefreshing}
        title="Update now"
        aria-label="Update now"
      >
        <Icon name="arrow-clockwise" />
      </button>
    </div>
  );
};

export default RefreshStatus;
